import { tabScopedStorage } from './preauth';

export type ElStatus = 'newcomer' | 'established' | 'ltel' | 'unknown';

export type Thresholds = {
  newcomerMaxYears: number;
  ltelMinYears: number;
  monitoringYears: number;
};

export type ThresholdStore = Pick<Storage, 'getItem' | 'setItem'>;

export const MONITORING_YEARS_MIN = 1;
export const MONITORING_YEARS_MAX = 10;
export const NEWCOMER_YEARS_MIN = 1;
export const NEWCOMER_YEARS_MAX = 3;
export const LTEL_YEARS_MIN = 4;
export const LTEL_YEARS_MAX = 9;

export const DEFAULT_THRESHOLDS: Thresholds = {
  newcomerMaxYears: 2,
  ltelMinYears: 6,
  monitoringYears: 4,
};

const STORAGE_KEY = 'bamboo.thresholds';

export function classifyElStatus(yearsInEl: number | null | undefined, t: Thresholds): ElStatus {
  if (yearsInEl == null || yearsInEl < 0) return 'unknown';
  if (yearsInEl <= t.newcomerMaxYears) return 'newcomer';
  if (yearsInEl >= t.ltelMinYears) return 'ltel';
  return 'established';
}

function browserStorage(): ThresholdStore | null {
  try {
    const s = tabScopedStorage();
    if (typeof s?.getItem === 'function' && typeof s?.setItem === 'function') return s;
    return null;
  } catch {
    return null;
  }
}

type StoredThresholds = { newcomerMaxYears: number; ltelMinYears: number; monitoringYears?: unknown };

function isSane(value: unknown): value is StoredThresholds {
  if (typeof value !== 'object' || value == null) return false;
  const t = value as Record<string, unknown>;
  if (
    typeof t.newcomerMaxYears !== 'number' ||
    typeof t.ltelMinYears !== 'number' ||
    !Number.isInteger(t.newcomerMaxYears) ||
    !Number.isInteger(t.ltelMinYears) ||
    t.newcomerMaxYears < 0 ||
    t.ltelMinYears <= t.newcomerMaxYears
  ) {
    return false;
  }
  return t.monitoringYears === undefined || isSaneMonitoringYears(t.monitoringYears);
}

export function isSaneMonitoringYears(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MONITORING_YEARS_MIN &&
    value <= MONITORING_YEARS_MAX
  );
}

export function loadThresholds(store: ThresholdStore | null = browserStorage()): Thresholds {
  try {
    const raw = store?.getItem(STORAGE_KEY);
    if (raw == null) return DEFAULT_THRESHOLDS;
    const parsed: unknown = JSON.parse(raw);
    if (!isSane(parsed)) return DEFAULT_THRESHOLDS;
    return {
      newcomerMaxYears: parsed.newcomerMaxYears,
      ltelMinYears: parsed.ltelMinYears,
      monitoringYears: isSaneMonitoringYears(parsed.monitoringYears)
        ? parsed.monitoringYears
        : DEFAULT_THRESHOLDS.monitoringYears,
    };
  } catch {
    return DEFAULT_THRESHOLDS;
  }
}

export function saveThresholds(t: Thresholds, store: ThresholdStore | null = browserStorage()): boolean {
  if (store == null) return false;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(t));
    return true;
  } catch {
    return false;
  }
}
